import React from 'react'

import { isEmpty } from 'lodash'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import { setNextCarePlanReviewDate } from 'src/store/modules/actionCreators'
import {
  expandedPatientInterventionList$,
  setAddActivityInterventionList,
  setExpandedPatientInterventionList,
  setGoalCategoryList,
  setGoalList,
  setInterventionList,
  setInterventionTypeList,
  setModalPatientGoalList,
  setPatientActiveCarePlanCount,
  setPatientBillingCodes,
  setPatientCarePlanChangeLog,
  setPatientCarePlanReviews,
  setPatientGoalList,
  setPatientGoalValue,
  setResponsibleList
} from 'src/store/modules/patient/carePlan'

import ErrorMessages from 'src/components/ErrorMessages'
import {
  CARE_PLAN_CHANGE_LOG_GET,
  CARE_PLAN_GET_RESPONSIBLE_LIST,
  CARE_PLAN_REVIEW_DATE_PATCH,
  CARE_PLAN_REVIEW_DELETE,
  CARE_PLAN_REVIEW_DELETE_MESSAGE,
  CARE_PLAN_REVIEW_GET,
  CARE_PLAN_REVIEW_POST,
  GET_BILLING_CODES,
  GOAL_CATEGORY_LIST_GET,
  GOAL_DELETE_MESSAGE,
  GOAL_LIST_GET,
  GOAL_SAVE_MESSAGE,
  INTERVENTION_DELETE_MESSAGE,
  INTERVENTION_GET,
  INTERVENTION_LIST_GET,
  INTERVENTION_SAVE_MESSAGE,
  INTERVENTION_TYPE_LIST_GET,
  PATIENT_GET_COUNT_ACTIVE_CARE_PLAN,
  PATIENT_GOALS,
  PATIENT_GOAL_GET_PATCH_DELETE,
  PATIENT_INTERVENTION_GET_PATCH_DELETE,
  PATIENT_INTERVENTION_LIST_GET,
  PATIENT_INTERVENTION_POST
} from 'src/constants'
import { showSuccessModal } from 'src/services/dialogs'
import { replaceKeyWords } from 'src/utils/String'

export const fetchResponsibleList = (config) => async (dispatch) => {
  const { params = {}, query = {} } = config

  try {
    const items = await httpClient.request({
      method: 'get',
      url: {
        pathname: CARE_PLAN_GET_RESPONSIBLE_LIST,
        params,
        query
      }
    })

    dispatch(setResponsibleList(items))
  } catch (err) {
    catchError(err)
  }
}

export const getGoals = async (config = {}) => {
  const { query = {} } = config

  try {
    const response = await httpClient.request({
      method: 'get',
      url: {
        pathname: GOAL_LIST_GET,
        query
      }
    })

    return response
  } catch (err) {
    catchError(err)

    return {}
  }
}

export const fetchGoalList = (config) => async (dispatch) => {
  try {
    const { items = [], meta = {} } = await getGoals(config)

    dispatch(setGoalList(items))

    return meta
  } catch (err) {
    catchError(err)
  }
}

export const fetchGoalCategoryList = (config) => async (dispatch) => {
  const { query = {} } = config

  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GOAL_CATEGORY_LIST_GET,
        query
      }
    })

    dispatch(setGoalCategoryList(items))
  } catch (err) {
    catchError(err)
  }
}

export const fetchPatientGoalList =
  (config = {}, isFromModal = false) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const response = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GOALS,
          params,
          query: {
            ...query,
            withInterventions: 1
          }
        }
      })
      const { items } = response

      if (!isFromModal) {
        dispatch(setPatientGoalList(items))
      } else {
        dispatch(setModalPatientGoalList(items))
      }

      return response
    } catch (err) {
      catchError(err)

      return {}
    }
  }

export const fetchPatientGoalValue = (config) => async (dispatch) => {
  const { params = {} } = config

  try {
    const item = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_GOAL_GET_PATCH_DELETE,
        params
      }
    })

    dispatch(setPatientGoalValue(item))
  } catch (err) {
    catchError(err)
  }
}

export const savePatientGoalValue = (config) => async () => {
  const { params = {}, payload = {}, pathname = PATIENT_GOALS } = config

  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname,
        params
      },
      payload
    })

    showSuccessModal({
      content: GOAL_SAVE_MESSAGE,
      centered: true,
      keyboard: false
    })

    return true
  } catch (err) {
    catchError(
      {
        ...err,
        message: <ErrorMessages errorObj={err} />
      },
      {
        width: 400
      }
    )

    return false
  }
}

export const updatePatientGoalValue =
  (config = {}, showModal = true) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATIENT_GOAL_GET_PATCH_DELETE,
          params
        },
        payload
      })

      if (showModal) {
        showSuccessModal({
          content: GOAL_SAVE_MESSAGE,
          centered: true,
          keyboard: false
        })
      }

      return true
    } catch (err) {
      catchError(
        {
          ...err,
          message: <ErrorMessages errorObj={err} />
        },
        {
          width: 400
        }
      )

      return false
    }
  }

export const deletePatientGoalValue = (config, replaceObj) => async () => {
  const { params = {} } = config

  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: PATIENT_GOAL_GET_PATCH_DELETE,
        params
      }
    })

    showSuccessModal({
      content: replaceKeyWords(GOAL_DELETE_MESSAGE, replaceObj),
      centered: true,
      keyboard: false
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const fetchInterventionList =
  (config = {}, isFromModal) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items = [], meta = {} } = await httpClient.request({
        method: 'get',
        url: {
          pathname: INTERVENTION_LIST_GET,
          query
        }
      })

      if (isFromModal) {
        dispatch(setAddActivityInterventionList(items))
      } else {
        dispatch(setInterventionList(items))
      }

      return meta
    } catch (err) {
      catchError(err)
    }
  }

export const fetchAddInterventionModalTasks =
  (config = {}) =>
  async (dispatch) => {
    const { params = {} } = config

    try {
      const data = await httpClient.request({
        method: 'get',
        url: {
          pathname: INTERVENTION_GET,
          params
        }
      })

      return data
    } catch (err) {
      catchError(err)
    }
  }

export const fetchInterventionTypeList = (config) => async (dispatch) => {
  const { query = {} } = config

  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: INTERVENTION_TYPE_LIST_GET,
        query
      }
    })

    dispatch(setInterventionTypeList(items))
  } catch (err) {
    catchError(err)
  }
}

export const fetchPatientInterventionList =
  (config, patientGoalUuid = '', isFromModal = false) =>
  async (dispatch, getState) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_INTERVENTION_LIST_GET,
          params,
          query
        }
      })

      if (isFromModal) {
        return items
      }

      const expandedPatientInterventionList = expandedPatientInterventionList$(
        getState(items)
      )
      const updExpandedPatientInterventionList = {
        ...expandedPatientInterventionList,
        [patientGoalUuid]: items
      }

      dispatch(
        setExpandedPatientInterventionList(updExpandedPatientInterventionList)
      )
    } catch (err) {
      catchError(err)
    }
  }

export const savePatientInterventionValue = (config) => async () => {
  const { params = {}, payload = {} } = config

  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: PATIENT_INTERVENTION_POST,
        params
      },
      payload
    })

    showSuccessModal({
      content: INTERVENTION_SAVE_MESSAGE,
      centered: true,
      keyboard: false
    })

    return true
  } catch (err) {
    catchError(
      {
        ...err,
        message: <ErrorMessages errorObj={err} />
      },
      {
        width: 400
      }
    )

    return false
  }
}

export const updatePatientInterventionValue =
  (config = {}, showModal = true) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATIENT_INTERVENTION_GET_PATCH_DELETE,
          params
        },
        payload
      })

      if (showModal) {
        showSuccessModal({
          content: INTERVENTION_SAVE_MESSAGE,
          centered: true,
          keyboard: false
        })
      }

      return true
    } catch (err) {
      catchError(
        {
          ...err,
          message: <ErrorMessages errorObj={err} />
        },
        {
          width: 400
        }
      )

      return false
    }
  }

export const deletePatientInterventionValue =
  (config, replaceObj) => async () => {
    const { params = {} } = config

    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: PATIENT_INTERVENTION_GET_PATCH_DELETE,
          params
        }
      })

      showSuccessModal({
        content: replaceKeyWords(INTERVENTION_DELETE_MESSAGE, replaceObj),
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const getPatientBillingCodes = (uuid) => async (dispatch) => {
  try {
    const codes = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_BILLING_CODES,
        params: { uuid }
      }
    })

    dispatch(setPatientBillingCodes(!isEmpty(codes) ? codes : {}))
  } catch (err) {
    catchError(err)
  }
}

export const createCarePlanReview = (config) => async () => {
  const { params = {}, payload = {} } = config

  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: CARE_PLAN_REVIEW_POST,
        params
      },
      payload
    })

    return true
  } catch (err) {
    catchError(
      {
        ...err,
        message: <ErrorMessages errorObj={err} />
      },
      {
        width: 400
      }
    )

    return false
  }
}

export const getCarePlanReviews =
  (uuid, query = {}) =>
  async (dispatch) => {
    try {
      const reviews = await httpClient.request({
        method: 'get',
        url: {
          pathname: CARE_PLAN_REVIEW_GET,
          params: { uuid },
          query
        }
      })

      dispatch(setPatientCarePlanReviews(reviews))
    } catch (err) {
      catchError(err)
    }
  }

export const getCarePlanLogs =
  (uuid, config = {}) =>
  async (dispatch) => {
    try {
      const { query } = config
      const logs = await httpClient.request({
        method: 'get',
        url: {
          pathname: CARE_PLAN_CHANGE_LOG_GET,
          params: { uuid },
          query
        }
      })

      dispatch(setPatientCarePlanChangeLog(logs))
    } catch (err) {
      catchError(err)
    }
  }

export const deleteCarePlanReview = (config, replaceObj) => async () => {
  const { params = {} } = config

  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: CARE_PLAN_REVIEW_DELETE,
        params
      }
    })

    showSuccessModal({
      content: replaceKeyWords(CARE_PLAN_REVIEW_DELETE_MESSAGE, replaceObj),
      centered: true,
      keyboard: false
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const fetchPatientActiveCarePlanCount =
  (config = {}) =>
  async (dispatch) => {
    const { params = {} } = config

    try {
      const response = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_COUNT_ACTIVE_CARE_PLAN,
          params
        }
      })
      const activeCarePlanCount = response?.[0] || {}

      dispatch(setPatientActiveCarePlanCount(activeCarePlanCount))
    } catch (err) {
      catchError(err)
    }
  }

export const updatePatientCarePlanReviewDate =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      const { nextCarePlanReviewDate } = await httpClient.request({
        method: 'patch',
        url: {
          pathname: CARE_PLAN_REVIEW_DATE_PATCH,
          params
        },
        payload
      })

      dispatch(setNextCarePlanReviewDate(nextCarePlanReviewDate))
    } catch (err) {
      catchError(err)
    }
  }
